/**
 * Visuel de partage : une image 1080 × 1920, le format story d'Instagram et de
 * Snapchat.
 *
 * Dessinée au canvas, sur l'appareil. Des chiffres de santé n'ont aucune raison
 * de transiter par un serveur pour devenir une image — pas même le nôtre — et
 * l'image existe ainsi avant que l'utilisateur décide où l'envoyer. Ni nom ni
 * adresse n'y figurent : on partage des chiffres, pas un compte.
 *
 * Les applications posent leur propre interface en haut et en bas d'une story
 * (profil, barre de réponse) : l'essentiel tient entre 250 et 1700 px. Le plus
 * petit texte fait 26 px, soit une dizaine de pixels CSS une fois l'image
 * ramenée à la largeur d'un téléphone.
 */

import * as fmt from './format.js';
import type { ShareSummary } from './share.js';
import { GOAL_LABELS, type VerdictKind } from './stats.js';
import type { Theme } from './theme.js';

export const SHARE_WIDTH = 1080;
export const SHARE_HEIGHT = 1920;

interface Palette {
  bg: string;
  card: string;
  line: string;
  ink: string;
  ink2: string;
  ink3: string;
  hair: string;
  /** --k : accent chaud, calories. */
  warm: string;
  /** --kt : le même, assez contrasté pour du texte. */
  warmText: string;
  /** --s : accent froid, pas. */
  cool: string;
}

/**
 * Jetons de styles.css. Le canvas ne lit pas les variables CSS, et l'image peut
 * prendre l'autre thème que l'écran : ils sont donc recopiés ici, les `oklch`
 * convertis en sRGB. À tenir alignés à la main.
 */
const PALETTES: Record<Theme, Palette> = {
  light: {
    bg: '#fbfbfa',
    card: '#ffffff',
    line: 'rgba(18, 18, 18, 0.1)',
    ink: '#161616',
    ink2: 'rgba(22, 22, 22, 0.6)',
    ink3: 'rgba(22, 22, 22, 0.66)',
    hair: 'rgba(22, 22, 22, 0.3)',
    warm: '#c74f45',
    warmText: '#ac312a',
    cool: '#2578a6',
  },
  dark: {
    bg: '#0c0c0d',
    card: '#15151a',
    line: 'rgba(255, 255, 255, 0.11)',
    ink: '#f3f3f4',
    ink2: 'rgba(243, 243, 244, 0.62)',
    ink3: 'rgba(243, 243, 244, 0.64)',
    hair: 'rgba(243, 243, 244, 0.34)',
    warm: '#f68978',
    warmText: '#ffa08f',
    cool: '#65b3e4',
  },
};

const SANS = '"Instrument Sans", system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO = '"Geist Mono", ui-monospace, "SFMono-Regular", Consolas, monospace';

/** Les graisses effectivement dessinées. */
const FACES = [
  '400 16px "Geist Mono"',
  '500 16px "Geist Mono"',
  '400 16px "Instrument Sans"',
  '500 16px "Instrument Sans"',
];

// --- Mise en page ------------------------------------------------------------

/** Marge latérale de l'image. */
const MARGIN = 80;
/** Marge intérieure des cartes. */
const PAD = 48;
const GAP = 24;
const RADIUS = 36;
const INNER = SHARE_WIDTH - 2 * MARGIN;
const LEFT = MARGIN + PAD;
const RIGHT = SHARE_WIDTH - MARGIN - PAD;

const BRAND_Y = 150;
const TITLE_Y = 256;
const RANGE_Y = 312;
const HERO = { top: 356, height: 312 };
const TILES = { top: HERO.top + HERO.height + GAP, height: 236 };
const TREND = { top: TILES.top + TILES.height + GAP, height: 140 };
const CHART = { top: TREND.top + TREND.height + GAP, height: 606 };
const FOOTNOTE_Y = CHART.top + CHART.height + 60;

// --- Primitives --------------------------------------------------------------

type Context = CanvasRenderingContext2D;

interface TextStyle {
  size: number;
  color: string;
  weight?: 400 | 500;
  family?: 'sans' | 'mono';
  /** Interlettrage, en fraction de la taille — comme `letter-spacing` en em. */
  spacing?: number;
  /** Détoure le texte de cette couleur, pour qu'un trait passant dessous ne le barre pas. */
  halo?: string;
}

type Align = 'left' | 'center' | 'right';

/** Petites capitales espacées des étiquettes, comme `.eyebrow` dans l'app. */
const eyebrow = (color: string): TextStyle => ({ size: 28, family: 'mono', color, spacing: 0.08 });

function apply(ctx: Context, style: TextStyle) {
  ctx.font = `${style.weight ?? 400} ${style.size}px ${style.family === 'mono' ? MONO : SANS}`;
  ctx.fillStyle = style.color;
  ctx.letterSpacing = `${(style.spacing ?? 0) * style.size}px`;
}

/**
 * Largeur réellement occupée. Le canvas compte l'interlettrage après le
 * dernier caractère aussi : un texte aligné à droite décollerait du bord
 * d'autant, d'où son retrait ici.
 */
function measure(ctx: Context, value: string, style: TextStyle): number {
  apply(ctx, style);
  return ctx.measureText(value).width - (style.spacing ?? 0) * style.size;
}

/** Écrit une ligne et rend sa largeur, pour enchaîner un second segment. */
function text(
  ctx: Context,
  value: string,
  x: number,
  y: number,
  style: TextStyle,
  align: Align = 'left',
): number {
  const width = measure(ctx, value, style);
  const start = align === 'left' ? x : align === 'right' ? x - width : x - width / 2;
  ctx.textAlign = 'left';
  if (style.halo) {
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = style.halo;
    ctx.strokeText(value, start, y);
  }
  ctx.fillText(value, start, y);
  ctx.letterSpacing = '0px';
  return width;
}

/**
 * Taille la plus grande, jusqu'à `style.size`, qui fait tenir le texte dans
 * `width`. Les chiffres viennent des saisies : rien ne garantit qu'un libellé
 * calibré sur des valeurs courantes ne déborde pas un jour de sa carte.
 */
function fitted(ctx: Context, value: string, width: number, style: TextStyle): TextStyle {
  let size = style.size;
  while (size > 12 && measure(ctx, value, { ...style, size }) > width) size -= 1;
  return { ...style, size };
}

type Run = [value: string, style: TextStyle];

/**
 * Plusieurs segments sur une même ligne — un chiffre en mono, ses mots en sans,
 * comme dans l'app — réduits d'un même facteur s'ils débordent de `width`.
 */
function runs(
  ctx: Context,
  parts: Run[],
  x: number,
  y: number,
  width = Number.POSITIVE_INFINITY,
  align: Align = 'left',
): number {
  // L'espace entre deux segments suit la taille du texte, comme une espace.
  const total = (scale: number) =>
    parts.reduce(
      (sum, [value, style], index) =>
        sum +
        measure(ctx, value, { ...style, size: style.size * scale }) +
        (index > 0 ? style.size * scale * 0.3 : 0),
      0,
    );
  const natural = total(1);
  const scale = natural > width ? width / natural : 1;
  const span = total(scale);

  let cursor = align === 'left' ? x : align === 'right' ? x - span : x - span / 2;
  parts.forEach(([value, style], index) => {
    const sized = { ...style, size: style.size * scale };
    if (index > 0) cursor += sized.size * 0.3;
    cursor += text(ctx, value, cursor, y, sized);
  });
  return span;
}

function card(
  ctx: Context,
  palette: Palette,
  top: number,
  height: number,
  left = MARGIN,
  width = INNER,
) {
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, RADIUS);
  ctx.fillStyle = palette.card;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = palette.line;
  ctx.stroke();
}

function dot(ctx: Context, x: number, y: number, radius: number, color: string) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

/** Pastille creuse d'une pesée : elle se distingue du trait plein de la moyenne. */
function ring(ctx: Context, x: number, y: number, palette: Palette) {
  dot(ctx, x, y, 9, palette.card);
  ctx.lineWidth = 4;
  ctx.strokeStyle = palette.ink;
  ctx.stroke();
}

function stroke(ctx: Context, x1: number, y1: number, x2: number, y2: number, color: string, width: number) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  ctx.stroke();
}

/** Ligne brisée d'une série ; un `null` lève le crayon, comme `linePath`. */
function polyline(
  ctx: Context,
  values: Array<number | null>,
  xOf: (index: number) => number,
  yOf: (value: number) => number,
  color: string,
  width: number,
) {
  ctx.beginPath();
  let pen = false;
  values.forEach((value, index) => {
    if (value == null) {
      pen = false;
      return;
    }
    if (pen) ctx.lineTo(xOf(index), yOf(value));
    else ctx.moveTo(xOf(index), yOf(value));
    pen = true;
  });
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  ctx.stroke();
}

/** `#rrggbb` → `rgba()`, pour les halos qui s'effacent vers la transparence. */
function withAlpha(hex: string, alpha: number): string {
  const value = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

/** Mêmes couleurs que le verdict de l'app : froid dans la cible, chaud hors. */
function verdictColor(kind: VerdictKind, palette: Palette): string {
  if (kind === 'in') return palette.cool;
  if (kind === 'out') return palette.warmText;
  return palette.ink3;
}

/** « DIM 12 » : le jour de la semaine compte autant que la date. */
const dayLabel = (date: Date): string =>
  `${fmt.date(date, { weekday: 'short' }).replace('.', '').toUpperCase()} ${date.getDate()}`;

// --- Blocs -------------------------------------------------------------------

function drawBackground(ctx: Context, palette: Palette, theme: Theme) {
  ctx.fillStyle = palette.bg;
  ctx.fillRect(0, 0, SHARE_WIDTH, SHARE_HEIGHT);

  // Deux halos aux couleurs des deux accents, en écho à la pastille de la
  // marque. Plus discrets en clair, où la même opacité salirait le fond.
  const strength = theme === 'dark' ? 0.2 : 0.12;
  const glows: Array<[string, number, number, number]> = [
    [palette.warm, SHARE_WIDTH - 60, 140, 620],
    [palette.cool, 40, SHARE_HEIGHT - 120, 640],
  ];
  for (const [color, x, y, radius] of glows) {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, withAlpha(color, strength));
    gradient.addColorStop(1, withAlpha(color, 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, SHARE_WIDTH, SHARE_HEIGHT);
  }
}

function drawHeader(ctx: Context, palette: Palette, summary: ShareSummary) {
  dot(ctx, MARGIN + 11, BRAND_Y - 11, 11, palette.warm);
  text(ctx, 'EPHEMERIS', MARGIN + 40, BRAND_Y, {
    size: 32,
    weight: 500,
    family: 'mono',
    color: palette.ink,
    spacing: 0.16,
  });
  text(ctx, 'BILAN 7 JOURS', SHARE_WIDTH - MARGIN, BRAND_Y, eyebrow(palette.ink3), 'right');

  text(ctx, 'Ma semaine', MARGIN - 4, TITLE_Y, {
    size: 96,
    weight: 500,
    color: palette.ink,
    spacing: -0.02,
  });
  text(ctx, fmt.spelledRange(summary.from, summary.to), MARGIN, RANGE_Y, {
    size: 38,
    color: palette.ink2,
  });
}

function drawHero(ctx: Context, palette: Palette, summary: ShareSummary) {
  const { top, height } = HERO;
  const { mean, delta, days } = summary.weight;
  card(ctx, palette, top, height);

  text(ctx, 'POIDS · MOYENNE 7 J', LEFT, top + 70, eyebrow(palette.ink3));
  text(ctx, `${days}/7 PESÉE${days > 1 ? 'S' : ''}`, RIGHT, top + 70, eyebrow(palette.ink3), 'right');

  const valueY = top + 208;
  if (mean == null) {
    text(ctx, '—', LEFT, valueY, { size: 156, weight: 500, family: 'mono', color: palette.ink3 });
  } else {
    runs(
      ctx,
      [
        [fmt.weight(mean), { size: 156, weight: 500, family: 'mono', color: palette.ink, spacing: -0.03 }],
        ['kg', { size: 52, family: 'mono', color: palette.ink3 }],
      ],
      LEFT - 6,
      valueY,
    );
  }

  const lineY = top + 264;
  const words = { size: 34, color: palette.ink2 };
  if (mean == null) {
    text(ctx, 'Moins de 4 pesées sur la semaine', LEFT, lineY, words);
  } else if (delta == null) {
    text(ctx, 'Semaine précédente : moins de 4 pesées', LEFT, lineY, words);
  } else {
    runs(
      ctx,
      [
        [`${fmt.delta(delta)} kg`, { size: 36, weight: 500, family: 'mono', color: palette.ink }],
        ['vs les 7 jours précédents', words],
      ],
      LEFT,
      lineY,
      RIGHT - LEFT,
    );
  }
}

function drawTiles(ctx: Context, palette: Palette, summary: ShareSummary) {
  const { top, height } = TILES;
  const width = (INNER - GAP) / 2;
  const figure: TextStyle = { size: 32, weight: 500, family: 'mono', color: palette.ink };
  const words: TextStyle = { size: 32, color: palette.ink2 };
  const { steps, kcal } = summary;

  const tiles: Array<{ label: string; color: string; value: number | null; note: Run[] }> = [
    {
      label: 'PAS / JOUR',
      color: palette.cool,
      value: steps.mean,
      note:
        steps.delta == null
          ? [['moyenne sur 7 jours', words]]
          : [
              [fmt.signedInteger(steps.delta), figure],
              ['vs sem. préc.', words],
            ],
    },
    {
      label: 'KCAL / JOUR',
      color: palette.warmText,
      value: kcal.mean,
      // L'écart au maintien dit plus que l'écart à la semaine passée : c'est
      // lui qui fait bouger la balance.
      note:
        kcal.gap == null
          ? [
              ['maintien', words],
              [fmt.number(kcal.maintenance), { ...figure, color: palette.ink2 }],
            ]
          : [
              [fmt.signedInteger(kcal.gap), figure],
              ['vs maintien', words],
              [fmt.number(kcal.maintenance), { ...figure, color: palette.ink2, weight: 400 }],
            ],
    },
  ];

  tiles.forEach((tile, index) => {
    const left = MARGIN + index * (width + GAP);
    const inner = width - 2 * PAD;
    card(ctx, palette, top, height, left, width);

    text(ctx, tile.label, left + PAD, top + 64, eyebrow(tile.color));
    const value = tile.value == null ? '—' : fmt.number(tile.value);
    text(
      ctx,
      value,
      left + PAD - 3,
      top + 150,
      fitted(ctx, value, inner, {
        size: 88,
        weight: 500,
        family: 'mono',
        color: tile.value == null ? palette.ink3 : palette.ink,
        spacing: -0.02,
      }),
    );
    runs(ctx, tile.note, left + PAD, top + 196, inner);
  });
}

function drawTrend(ctx: Context, palette: Palette, summary: ShareSummary) {
  const { top, height } = TREND;
  const { slope, window } = summary.trend;
  card(ctx, palette, top, height);

  text(
    ctx,
    `TENDANCE ${window} J · ${GOAL_LABELS[summary.goal].toUpperCase()}`,
    LEFT,
    top + 60,
    eyebrow(palette.ink3),
  );

  if (!slope) {
    text(ctx, 'Pas encore assez de pesées', LEFT, top + 108, { size: 36, color: palette.ink2 });
    text(ctx, '—', RIGHT, top + 88, { size: 60, weight: 500, family: 'mono', color: palette.ink3 }, 'right');
    return;
  }

  const color = verdictColor(slope.verdict, palette);
  const figure = runs(
    ctx,
    [
      [fmt.delta(slope.perWeek, 2), { size: 60, weight: 500, family: 'mono', color, spacing: -0.02 }],
      ['kg/sem', { size: 28, family: 'mono', color: palette.ink3 }],
    ],
    RIGHT,
    top + 72,
    Number.POSITIVE_INFINITY,
    'right',
  );
  text(
    ctx,
    `± ${fmt.weight(slope.margin, 2)} · IC 95 %`,
    RIGHT,
    top + 112,
    { size: 28, family: 'mono', color: palette.ink3 },
    'right',
  );

  // Le verdict prend la place que lui laisse le chiffre, jamais davantage.
  const room = RIGHT - LEFT - figure - 32;
  text(ctx, slope.text, LEFT, top + 108, fitted(ctx, slope.text, room, { size: 36, weight: 500, color }));
}

/**
 * Échelle verticale des pesées : un kilo d'amplitude au moins, sans quoi
 * quelques grammes d'écart dessineraient une chute.
 */
function weightScale(values: Array<number | null>): { low: number; high: number } | null {
  const known = values.filter((value): value is number => value != null);
  if (known.length === 0) return null;
  const low = Math.min(...known);
  const high = Math.max(...known);
  const middle = (low + high) / 2;
  const half = Math.max((high - low) / 2, 0.5);
  return { low: middle - half, high: middle + half };
}

/** Repères verticaux du graphe, relatifs au haut de sa carte. */
const PLOT = {
  header: 72,
  weightTop: 150,
  // Une étiquette peut tomber sous la pesée la plus basse : la place est
  // réservée avant les barres de pas.
  weightBottom: 222,
  stepsBase: 384,
  kcalBase: 516,
  barHeight: 64,
  days: 568,
};

function drawChart(ctx: Context, palette: Palette, summary: ShareSummary) {
  const { top, height } = CHART;
  card(ctx, palette, top, height);

  // La légende tient lieu de titre : les dates en pied de carte disent déjà
  // qu'on lit la semaine jour par jour. Chaque série garde la couleur de sa
  // carte et de ses étiquettes ; ses entrées sont réparties sur la largeur.
  const header = top + PLOT.header;
  const label = { ...eyebrow(palette.ink3), size: 26 };
  const legend: Array<{ name: string; marker: number; draw: (x: number, y: number) => void }> = [
    { name: 'PESÉE', marker: 22, draw: (x, y) => ring(ctx, x + 11, y, palette) },
    { name: 'MOY. 7 J', marker: 30, draw: (x, y) => stroke(ctx, x, y, x + 30, y, palette.ink, 7) },
    { name: 'PAS', marker: 16, draw: (x, y) => dot(ctx, x + 8, y, 8, palette.cool) },
    { name: 'KCAL', marker: 16, draw: (x, y) => dot(ctx, x + 8, y, 8, palette.warm) },
  ];
  const widths = legend.map((item) => item.marker + 12 + measure(ctx, item.name, label));
  const spread = (RIGHT - LEFT - widths.reduce((sum, width) => sum + width, 0)) / (legend.length - 1);
  let cursor = LEFT;
  legend.forEach((item, index) => {
    item.draw(cursor, header - 9);
    text(ctx, item.name, cursor + item.marker + 12, header, label);
    cursor += (widths[index] ?? 0) + spread;
  });

  const { days } = summary;
  const { average } = summary.weight;
  const column = (RIGHT - LEFT) / Math.max(days.length, 1);
  const centre = (index: number) => LEFT + column * (index + 0.5);
  const tag = (color: string): TextStyle => ({ size: 28, weight: 500, family: 'mono', color });
  const missing: TextStyle = { size: 28, family: 'mono', color: palette.ink3 };

  // Poids : la moyenne 7 jours en trait épais, les pesées brutes en trait fin
  // par-dessus — la lecture de l'évolution comparée. C'est le trait épais
  // qu'il faut suivre : d'un jour à l'autre, une pesée bouge de l'eau, pas de
  // la graisse. Un jour sans valeur coupe le tracé ; rien n'est interpolé.
  const weights = days.map((row) => row.weight);
  const plotTop = top + PLOT.weightTop;
  const plotBottom = top + PLOT.weightBottom;
  const scale = weightScale([...weights, ...average]);
  const yOf = (value: number) =>
    scale == null
      ? plotBottom
      : plotBottom - ((value - scale.low) / (scale.high - scale.low)) * (plotBottom - plotTop);

  polyline(ctx, average, centre, yOf, palette.ink, 7);
  polyline(ctx, weights, centre, yOf, palette.hair, 3);

  // Chaque étiquette se pose du côté de la pesée opposé à la moyenne — au-dessus
  // d'une pesée plus haute, en dessous d'une plus basse — si bien que le trait
  // épais ne la barre jamais. Le détourage ne sert plus qu'au trait fin.
  const above = (y: number) => y - 26;
  const below = (y: number) => y + 46;
  weights.forEach((weight, index) => {
    const x = centre(index);
    const mean = average[index];
    if (weight == null) {
      // Le tiret prend la place d'une étiquette, pas celle du trait.
      const y = mean == null ? (plotTop + plotBottom) / 2 + 10 : above(yOf(mean));
      text(ctx, '—', x, y, { ...missing, halo: palette.card }, 'center');
      return;
    }
    const y = yOf(weight);
    ring(ctx, x, y, palette);
    const labelY = mean != null && weight < mean ? below(y) : above(y);
    text(ctx, fmt.measuredWeight(weight), x, labelY, { ...tag(palette.ink), halo: palette.card }, 'center');
  });

  // Pas puis calories : des barres partant de zéro, chacune à l'échelle de sa
  // propre semaine — on compare les jours entre eux, comme le calendrier.
  const bands: Array<{ key: 'steps' | 'kcal'; base: number; fill: string; ink: string }> = [
    { key: 'steps', base: top + PLOT.stepsBase, fill: palette.cool, ink: palette.cool },
    { key: 'kcal', base: top + PLOT.kcalBase, fill: palette.warm, ink: palette.warmText },
  ];
  const barWidth = Math.min(64, column * 0.55);

  for (const band of bands) {
    const values = days.map((row) => row[band.key]);
    const peak = Math.max(1, ...values.map((value) => value ?? 0));

    ctx.fillStyle = palette.line;
    ctx.fillRect(LEFT, band.base, RIGHT - LEFT, 2);

    values.forEach((value, index) => {
      const x = centre(index);
      if (value == null) {
        text(ctx, '—', x, band.base - 16, missing, 'center');
        return;
      }
      const barHeight = Math.max(6, (value / peak) * PLOT.barHeight);
      ctx.beginPath();
      ctx.roundRect(x - barWidth / 2, band.base - barHeight, barWidth, barHeight, [10, 10, 0, 0]);
      ctx.fillStyle = band.fill;
      ctx.fill();
      const label = fmt.number(value);
      text(ctx, label, x, band.base - barHeight - 14, fitted(ctx, label, column - 8, tag(band.ink)), 'center');
    });
  }

  const last = days.length - 1;
  days.forEach((row, index) => {
    const today = index === last;
    text(
      ctx,
      dayLabel(row.date),
      centre(index),
      top + PLOT.days,
      {
        size: 26,
        weight: today ? 500 : 400,
        family: 'mono',
        color: today ? palette.ink : palette.ink3,
      },
      'center',
    );
  });
}

function drawFootnote(ctx: Context, palette: Palette) {
  text(
    ctx,
    'Pesée du matin · pas et calories de la veille',
    SHARE_WIDTH / 2,
    FOOTNOTE_Y,
    { size: 26, family: 'mono', color: palette.ink3 },
    'center',
  );
}

// --- Rendu ---------------------------------------------------------------------

/** Les polices sont auto-hébergées ; une absente se dessine avec le repli. */
async function loadFonts(): Promise<void> {
  await Promise.all(FACES.map((face) => document.fonts.load(face).catch(() => [])));
}

export async function renderShareImage(summary: ShareSummary, theme: Theme): Promise<Blob> {
  await loadFonts();

  const canvas = document.createElement('canvas');
  canvas.width = SHARE_WIDTH;
  canvas.height = SHARE_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Ce navigateur ne sait pas dessiner l’image.');

  const palette = PALETTES[theme];
  drawBackground(ctx, palette, theme);
  drawHeader(ctx, palette, summary);
  drawHero(ctx, palette, summary);
  drawTiles(ctx, palette, summary);
  drawTrend(ctx, palette, summary);
  drawChart(ctx, palette, summary);
  drawFootnote(ctx, palette);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Image impossible à produire.'))),
      'image/png',
    );
  });
}
